import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  openWhatsApp, 
  WhatsAppTemplates, 
  WhatsAppTemplateOptions,
  cleanPhoneNumber 
} from '@/utils/whatsapp';
import { MessageCircle, Send, ExternalLink } from 'lucide-react';

// WhatsApp Icon Component
export const WhatsAppIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Simple WhatsApp button that opens with greeting
export function WhatsAppButton({ 
  phone, 
  clientName = 'Cliente',
  size = 'default',
  variant = 'outline',
  showText = true,
  className = ''
}) {
  const handleClick = () => {
    if (!phone) {
      toast.error('Cliente não tem número de telefone');
      return;
    }
    
    const cleaned = cleanPhoneNumber(phone);
    if (!cleaned) {
      toast.error('Número de telefone inválido');
      return;
    }
    
    const message = WhatsAppTemplates.greeting(clientName);
    openWhatsApp(phone, message);
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      className={`text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 ${className}`}
      title="Enviar WhatsApp"
    >
      <WhatsAppIcon className="h-4 w-4" />
      {showText && <span className="ml-2">WhatsApp</span>}
    </Button>
  );
}

// WhatsApp button with template selector modal
export function WhatsAppButtonWithTemplates({ 
  phone, 
  clientName = 'Cliente',
  orderId = null,
  orderTotal = null,
  visitDate = null,
  visitTime = null,
  size = 'default',
  variant = 'outline',
  showText = true,
  className = '',
  defaultTemplate = 'greeting'
}) {
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate);
  const [customMessage, setCustomMessage] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');

  const generateMessage = (templateId) => {
    switch (templateId) {
      case 'greeting':
        return WhatsAppTemplates.greeting(clientName);
      case 'orderConfirmation':
        return WhatsAppTemplates.orderConfirmation(clientName, orderId || '---', orderTotal || '---');
      case 'orderReady':
        return WhatsAppTemplates.orderReady(clientName, orderId || '---');
      case 'visitReminder':
        return WhatsAppTemplates.visitReminder(clientName, visitDate || '---', visitTime);
      case 'visitingToday':
        return WhatsAppTemplates.visitingToday(clientName);
      case 'followUpVisit':
        return WhatsAppTemplates.followUpVisit(clientName);
      case 'proposalSent':
        return WhatsAppTemplates.proposalSent(clientName, orderId || '---');
      case 'paymentReminder':
        return WhatsAppTemplates.paymentReminder(clientName, orderTotal || '---', visitDate || '---');
      case 'newProduct':
        return WhatsAppTemplates.newProduct(clientName, 'Produto');
      case 'thankYou':
        return WhatsAppTemplates.thankYou(clientName);
      case 'custom':
        return WhatsAppTemplates.custom(clientName, customMessage || 'Sua mensagem aqui...');
      default:
        return WhatsAppTemplates.greeting(clientName);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setPreviewMessage(generateMessage(templateId));
  };

  const handleOpenModal = () => {
    if (!phone) {
      toast.error('Cliente não tem número de telefone');
      return;
    }
    setPreviewMessage(generateMessage(selectedTemplate));
    setShowModal(true);
  };

  const handleSend = () => {
    let message = previewMessage;
    if (selectedTemplate === 'custom') {
      message = WhatsAppTemplates.custom(clientName, customMessage);
    }
    openWhatsApp(phone, message);
    setShowModal(false);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleOpenModal}
        className={`text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 ${className}`}
        title="Enviar WhatsApp"
      >
        <WhatsAppIcon className="h-4 w-4" />
        {showText && <span className="ml-2">WhatsApp</span>}
      </Button>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="h-5 w-5 text-emerald-500" />
              Enviar WhatsApp para {clientName}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden space-y-4 py-2">
            {/* Template Selector */}
            <div className="space-y-2">
              <Label>Modelo de Mensagem</Label>
              <ScrollArea className="h-[150px] border rounded-lg p-2">
                <div className="grid grid-cols-2 gap-2">
                  {WhatsAppTemplateOptions.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      className={`p-2 text-left rounded-lg border transition-colors text-sm
                        ${selectedTemplate === template.id 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-emerald-300'
                        }`}
                    >
                      <span className="mr-1">{template.icon}</span>
                      {template.label}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Custom Message Input */}
            {selectedTemplate === 'custom' && (
              <div className="space-y-2">
                <Label>Mensagem Personalizada</Label>
                <Textarea
                  value={customMessage}
                  onChange={(e) => {
                    setCustomMessage(e.target.value);
                    setPreviewMessage(WhatsAppTemplates.custom(clientName, e.target.value));
                  }}
                  placeholder="Escreva a sua mensagem..."
                  rows={3}
                />
              </div>
            )}

            {/* Preview */}
            <div className="space-y-2">
              <Label>Pré-visualização</Label>
              <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 whitespace-pre-wrap text-sm max-h-[150px] overflow-y-auto">
                {previewMessage}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSend}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Quick WhatsApp send for specific actions
export function QuickWhatsApp({ 
  phone, 
  clientName, 
  template, 
  templateData = {},
  children,
  className = ''
}) {
  const handleClick = () => {
    if (!phone) {
      toast.error('Cliente não tem número de telefone');
      return;
    }

    let message;
    switch (template) {
      case 'orderConfirmation':
        message = WhatsAppTemplates.orderConfirmation(
          clientName, 
          templateData.orderId, 
          templateData.total
        );
        break;
      case 'orderReady':
        message = WhatsAppTemplates.orderReady(clientName, templateData.orderId);
        break;
      case 'visitReminder':
        message = WhatsAppTemplates.visitReminder(
          clientName, 
          templateData.date, 
          templateData.time
        );
        break;
      case 'visitingToday':
        message = WhatsAppTemplates.visitingToday(clientName);
        break;
      default:
        message = WhatsAppTemplates.greeting(clientName);
    }

    openWhatsApp(phone, message);
  };

  return (
    <span onClick={handleClick} className={`cursor-pointer ${className}`}>
      {children}
    </span>
  );
}

export default WhatsAppButton;
